import React from 'react';
import UnifiedSimpleConversionExplanation from './UnifiedSimpleConversionExplanation';

const ResistanceConversionExplanation = ({
  fromUnit,
  toUnit,
  inputValue,
  result,
  darkMode
}) => {
  const getConversionData = () => {
    // Ohms to other units
    if (fromUnit === 'Ohms' && toUnit === 'Kilohms') {
      return {
        formula: 'kΩ = Ω ÷ 1000',
        conversionSteps: [
          `Take the resistance in ohms: ${inputValue}Ω`,
          `Divide by 1000: ${inputValue} ÷ 1000 = ${result}kΩ`
        ],
        realWorldExample: "A typical pull-up resistor might be 10kΩ (10,000 ohms).",
        funFacts: [
          "The ohm (Ω) is named after Georg Ohm, who discovered Ohm's Law.",
          "The symbol Ω is the last letter of the Greek alphabet, omega.",
          "Resistors are color-coded to indicate their resistance value.",
        ]
      };
    }

    if (fromUnit === 'Ohms' && toUnit === 'Megohms') {
      return {
        formula: 'MΩ = Ω ÷ 1,000,000',
        conversionSteps: [
          `Take the resistance in ohms: ${inputValue}Ω`,
          `Divide by 1,000,000: ${inputValue} ÷ 1,000,000 = ${result}MΩ`
        ],
        realWorldExample: "Electrical insulation typically has resistance in the megohm range.",
        funFacts: [
          "High-quality electrical insulators can have resistance of many gigohms.",
          "Megohm values are often used in high-voltage applications.",
          "Static discharge protection often uses megohm-range resistors.",
        ]
      };
    }

    if (fromUnit === 'Milliohms' && toUnit === 'Ohms') {
      return {
        formula: 'Ω = mΩ ÷ 1000',
        conversionSteps: [
          `Take the resistance in milliohms: ${inputValue}mΩ`,
          `Divide by 1000: ${inputValue} ÷ 1000 = ${result}Ω`
        ],
        realWorldExample: "Current sense resistors often have values in milliohms.",
        funFacts: [
          "Milliohm measurements are crucial in power supply design.",
          "PCB trace resistance is often measured in milliohms.",
          "Low resistance measurements require special techniques to be accurate.",
        ]
      };
    }

    if (fromUnit === 'Microohms' && toUnit === 'Milliohms') {
      return {
        formula: 'mΩ = µΩ ÷ 1000',
        conversionSteps: [
          `Take the resistance in microohms: ${inputValue}µΩ`,
          `Divide by 1000: ${inputValue} ÷ 1000 = ${result}mΩ`
        ],
        realWorldExample: "Superconductor resistance is measured in microohms or less.",
        funFacts: [
          "Contact resistance between metals can be in the microohm range.",
          "High-current applications often deal with microohm-level resistances.",
          "Precision measurements at this level require four-wire sensing.",
        ]
      };
    }

    if (fromUnit === 'Gigaohms' && toUnit === 'Megohms') {
      return {
        formula: 'MΩ = GΩ × 1000',
        conversionSteps: [
          `Take the resistance in gigaohms: ${inputValue}GΩ`,
          `Multiply by 1000: ${inputValue} × 1000 = ${result}MΩ`
        ],
        realWorldExample: "Ultra-high impedance measurements in scientific instruments.",
        funFacts: [
          "Air can have resistance in the gigaohm range.",
          "Gigaohm resistances are used in electrometer circuits.",
          "Static charge can create gigaohm-level impedances.",
        ]
      };
    }

    // Special cases for theoretical units
    if (fromUnit === 'Abohms' || toUnit === 'Statohms') {
      return {
        formula: 'Complex conversion involving electromagnetic units',
        conversionSteps: [
          `Take the resistance in ${fromUnit}: ${inputValue}`,
          `Apply appropriate conversion factor`,
          `Result: ${result} ${toUnit}`
        ],
        realWorldExample: "These units are mainly used in theoretical physics calculations.",
        funFacts: [
          "Abohms and statohms are part of the CGS system of units.",
          "These units are rarely used in practical electronics.",
          "They're important in understanding the historical development of electrical theory.",
        ]
      };
    }

    // Default case for other conversions
    return {
      formula: `Converting from ${fromUnit} to ${toUnit}`,
      conversionSteps: [
        `Start with ${inputValue} ${fromUnit}`,
        `Apply conversion factor`,
        `Result: ${result} ${toUnit}`
      ],
      realWorldExample: "Resistance measurements are crucial in electronic circuit design and testing.",
      funFacts: [
        "Resistance is the opposition to electric current flow.",
        "All materials have some level of electrical resistance.",
        "Temperature usually affects a material's resistance.",
      ]
    };
  };

  const conversionData = getConversionData();

  return (
    <UnifiedSimpleConversionExplanation
      conversionType="resistance"
      fromUnit={fromUnit}
      toUnit={toUnit}
      inputValue={inputValue}
      result={result}
      darkMode={darkMode}
      {...conversionData}
    />
  );
};

export default ResistanceConversionExplanation; 